import { useCallback, useRef, useState } from 'react';

import {
  followEveryMessage,
  followMessagesAtBottom,
  PREFIX_ID,
  SUFFIX_ID,
} from 'chat-viewer';
import type { ChatViewerHandle, OnMessagesCallback } from 'chat-viewer';

import {
  MessageType,
  Sender,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import { selectMessages } from '@/features';
import type { Message } from '@/features/messages/model';
import { useAppSelector } from '@/store';

const SCROLL_BUTTON_BOTTOM_THRESHOLD = 2;

/** Virtual list index for a message id (handles string/number id vs Map key). */
function listIndexForMessageId(
  chat: ChatViewerHandle<Message>,
  targetId: string,
): number | undefined {
  const normalized = String(targetId);
  let best: number | undefined;
  for (const [index, mid] of chat.indexesToIds) {
    if (mid === PREFIX_ID || mid === SUFFIX_ID) {
      continue;
    }
    if (String(mid) === normalized) {
      if (best === undefined || index < best) {
        best = index;
      }
    }
  }
  return best;
}

export function useChatPendingNewMessages(isEditor: boolean) {
  const messages = useAppSelector(selectMessages.visibleMessages);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const unreadMessageIdsRef = useRef<string[]>([]);

  const [pendingCount, setPendingCount] = useState(0);
  const [isPastBottomThreshold, setIsPastBottomThreshold] = useState(false);
  const [dividerBeforeMessageId, setDividerBeforeMessageId] = useState<
    string | null
  >(null);

  const dividerBeforeMessageIdRef = useRef(dividerBeforeMessageId);
  dividerBeforeMessageIdRef.current = dividerBeforeMessageId;

  const clearScrollBadge = useCallback(() => {
    setPendingCount(0);
  }, []);

  const onAtBottom = useCallback(() => {
    clearScrollBadge();
    unreadMessageIdsRef.current = [];
    setIsPastBottomThreshold(false);
  }, [clearScrollBadge]);

  const onScrollPositionChange = useCallback(
    (chat: ChatViewerHandle<Message>, _scrollOffset: number) => {
      const unreadMessageIds = unreadMessageIdsRef.current.filter(
        (messageId) => !chat.isIdInViewport(messageId),
      );

      if (unreadMessageIds.length !== unreadMessageIdsRef.current.length) {
        unreadMessageIdsRef.current = unreadMessageIds;
        setPendingCount(unreadMessageIds.length);
      }

      if (chat.atBottom) {
        unreadMessageIdsRef.current = [];
        setIsPastBottomThreshold(false);
        return;
      }

      const newestIndexInViewport = chat.newestIndexInViewport;
      const lastMessageIndex = messagesRef.current.length - 1;
      const messagesBelowViewport = lastMessageIndex - newestIndexInViewport;

      setIsPastBottomThreshold(
        Number.isFinite(newestIndexInViewport) &&
          messagesBelowViewport >= SCROLL_BUTTON_BOTTOM_THRESHOLD,
      );
    },
    [],
  );

  const scrollToBottomAndClear = useCallback(
    (chat: ChatViewerHandle<Message>) => {
      clearScrollBadge();
      const firstUnreadId = unreadMessageIdsRef.current[0] ?? null;
      const scroll = () => {
        if (firstUnreadId !== null) {
          const listIndex = listIndexForMessageId(chat, firstUnreadId);
          if (listIndex !== undefined) {
            chat.scrollToIndex(listIndex, { align: 'start', smooth: true });
            return;
          }
        }
        chat.scrollToBottom({ align: 'end', smooth: true });
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(scroll);
      });
    },
    [clearScrollBadge],
  );

  const onNewerMessages: OnMessagesCallback<Message> = useCallback(
    (chat, received) => {
      if (isEditor) {
        chat.scrollToTop({ align: 'end', smooth: true });
        return;
      }

      const lastMessage = received.at(-1);

      const isContactForm =
        lastMessage?.messageType === MessageType.CONTACT_FORM;

      if (isContactForm && lastMessage) {
        chat.scrollToId(lastMessage.id, { align: 'start' });
        return;
      }

      const hasUserMessages = received.some(
        (message) => message.sender === Sender.SITE_VISITOR,
      );

      if (hasUserMessages) {
        followEveryMessage<Message>()(chat, received);
        return;
      }

      const wasAtBottom = chat.atBottom;

      followMessagesAtBottom<Message>()(chat, received);

      const lastReceived = received.at(-1);
      if (!lastReceived) {
        return;
      }

      const schedulePendingCheck = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const list = messagesRef.current;
            const firstNewIndex = list.length - received.length;
            const anchor = list[firstNewIndex];
            if (!anchor) {
              return;
            }

            const lastId = lastReceived.id;
            const idRegistered = chat.idsToIndexes.has(lastId);
            const lastInView = idRegistered && chat.isIdInViewport(lastId);

            /* New tail is visible: at bottom before append, scrolled to bottom, or newest in viewport. */
            if (wasAtBottom || chat.atBottom || lastInView) {
              return;
            }

            /* Scrolled up: stack badge and pin divider above first unseen message. */
            const unreadBeforeAppend = unreadMessageIdsRef.current.length;
            unreadMessageIdsRef.current = [
              ...unreadMessageIdsRef.current,
              ...received.map((message) => String(message.id)),
            ];
            setPendingCount(unreadMessageIdsRef.current.length);
            if (unreadBeforeAppend === 0) {
              setDividerBeforeMessageId(String(anchor.id));
            }
          });
        });
      };
      schedulePendingCheck();
    },
    [isEditor],
  );

  return {
    pendingCount,
    showScrollToNewMessagesButton: pendingCount > 0 || isPastBottomThreshold,
    dividerBeforeMessageId,
    onNewerMessages,
    onAtBottom,
    onScrollPositionChange,
    scrollToBottomAndClear,
  };
}
